import type { ShapeProps, Slide, TextProps } from '@features/powerpoint';
import { chartOptionsToBase64Svg } from '@features/powerpoint';
import type { Lci3SalesMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { formatters } from '@utils/formattersV2';
import { getSalesLiftChartSVG } from '../charts';
import { themeV2 } from '../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../utils/slide-header-v2';

type Metric = 'inStore' | 'online' | 'total';

type SalesLiftSummaryChartSlideV2Props = {
  slideMaster: string;
  overallSalesMetrics: Lci3SalesMetrics;
  iroas: number;
};

const fieldHeaderOptions: TextProps['options'] = {
  fontSize: 12,
  bold: true,
  fontFace: DEFAULT_FONT,
  color: themeV2.colors.metricBlue,
};

const fieldValueOptions: TextProps['options'] = {
  color: themeV2.colors.metricBlue,
  fontFace: DEFAULT_FONT,
  h: 0.2,
  w: 3.26,
  fontSize: 20,
  bold: true,
};

const createLegendItem = (
  title: string,
  salesMetricKey: Metric,
  overallSalesMetrics: Lci3SalesMetrics,
  options: TextProps['options']
): TextProps => ({
  text: [
    {
      text: title,
      options: {
        ...fieldHeaderOptions,
        breakLine: true,
      },
    },
    {
      text: formatters.lift(
        overallSalesMetrics.incrementalSalesLift[salesMetricKey],
        { decimals: 0 }
      ),
      options: {
        ...fieldValueOptions,
        breakLine: true,
      },
    },
    {
      text: `Confidence Level: ${formatters.confidenceLevel(
        overallSalesMetrics.confidenceLevel[salesMetricKey],
        overallSalesMetrics.incrementalSalesLift[salesMetricKey]
      )}`,
      options: {
        breakLine: true,
        fontSize: 10,
        fontFace: DEFAULT_FONT,
        color: themeV2.colors.metricBlue,
      },
    },
  ],
  options,
});

export const salesLiftSummaryChartSlideV2 = (
  props: SalesLiftSummaryChartSlideV2Props
): Slide => {
  const salesLiftChartSVG = chartOptionsToBase64Svg(
    getSalesLiftChartSVG(props.overallSalesMetrics, 400, 400)
  );

  const overallText = createLegendItem(
    'Overall',
    'total',
    props.overallSalesMetrics,
    {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.02,
      w: 2.63,
      x: 0.94,
      y: 2.2,
    }
  );

  const inStoreText = createLegendItem(
    'In-Store',
    'inStore',
    props.overallSalesMetrics,
    {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.02,
      w: 2.63,
      x: 9.9,
      y: 2.2,
    }
  );

  const eCommerceText = createLegendItem(
    'E-Commerce',
    'online',
    props.overallSalesMetrics,
    {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.02,
      w: 2.63,
      x: 9.9,
      y: 3.36,
    }
  );

  const incrementalSalesHeaderText: TextProps = {
    text: 'Incremental Sales',
    options: {
      fontSize: 16,
      bold: true,
      align: 'center',
      fontFace: DEFAULT_FONT,
      color: themeV2.colors.metricBlue,
      h: 0.38,
      w: 2.33,
      x: 4.63,
      y: 1.63,
    },
  };

  const iroasText: TextProps = {
    text: [
      {
        text: 'iROAS',
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: ' (Incremental Return on Ad Spend)',
        options: {
          fontSize: 9,
          breakLine: true,
          fontFace: DEFAULT_FONT,
          color: themeV2.colors.metricBlue,
        },
      },
      {
        text: formatters.currency(props.iroas, { decimals: 2 }),
        options: {
          ...fieldValueOptions,
        },
      },
    ],
    options: {
      x: 0.94,
      h: 1.02,
      w: 4,
      y: 3.36,
      lineSpacingMultiple: 1.2,
      valign: 'top',
    },
  };

  const shapes: ShapeProps[] = [
    {
      shapeName: 'rect',
      options: {
        fill: { color: themeV2.colors.magnetBlue },
        h: 0.29,
        w: 0.29,
        x: 0.58,
        y: 2.56,
      },
    },
    {
      shapeName: 'rect',
      options: {
        fill: { color: themeV2.colors.inmarketGreen },
        h: 0.29,
        w: 0.29,
        x: 9.54,
        y: 2.56,
      },
    },
    {
      shapeName: 'rect',
      options: {
        fill: { color: themeV2.colors.commandBlue },
        h: 0.29,
        w: 0.29,
        x: 9.54,
        y: 3.72,
      },
    },
  ];

  const images = [
    {
      data: salesLiftChartSVG,
      h: 4.96,
      w: 4.96,
      x: 4.22,
      y: 1.82,
    },
  ];

  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeaderV2('Sales Lift Summary'),
      {
        text: 'Incremental Sales Lift',
        options: {
          fontSize: 16,
          bold: true,
          fontFace: DEFAULT_FONT,
          color: themeV2.colors.metricBlue,
          x: 0.94,
          y: 1.85,
          w: 3.16,
          h: 0.47,
        },
      },
      incrementalSalesHeaderText,
      overallText,
      inStoreText,
      eCommerceText,
      iroasText,
    ],
    shapes,
    images,
  };
};
